import { computed, ref } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import { defineStore } from 'pinia'
import { QueryType } from '../components/TargetingListsPageAnalyzeData.vue'
import {
  DeepThoughtXStatsTrend,
  StandAloneComputeAgeGroups,
  StandaloneExploreExtractDataTweetKeywords,
  StandaloneExploreQueryAnalyzer,
  StandaloneHandle,
  StandaloneInsightsAnalyzer,
  StandaloneInsightsAnalyzerInput,
  StandAlonePostActivity,
  StandAloneShareOfVoice,
  StandaloneXConversation,
  StandaloneXConversationInput,
} from '@/api'

const STORE_KEY = 'protargeting-analyze'

export const useAnalyzeStore = defineStore(STORE_KEY, () => {
  const loading = ref(false)
  const loadingPosts = ref(false)
  const loadingSentiments = ref(false)
  const loadingConversation = ref(false)
  const loadingRelevantConfigs = ref(false)
  const loadingEnrichments = ref(false)
  const loadingPostMetadata = ref(false)
  const loadingSynonyms = ref(false)
  const loadingShareOfVoice = ref(false)
  const loadingTrendingTopics = ref(false)
  const takingTooLong = ref(false)
  const tookTooLong = ref(false)
  const tookTooLongAccounts = ref(false)
  const tookTooLongPosts = ref(false)
  const tookTooLongEnrichments = ref(false)
  const tookTooLongDemographics = ref(false)
  const tookTooLongAge = ref(false)
  const isOpenRequest = ref(false)
  const isOpenEmail = ref(false)
  const submittedHandle = ref<
    string[] | Pick<StandaloneXConversation, 'keywords'> | StandaloneInsightsAnalyzerInput['query']
  >('')
  const handle = ref('')
  const similarHandles = ref<StandaloneHandle[]>()
  const error = ref('')
  const synonyms = ref<string[]>([])
  const shareOfVoiceWithKeywords = ref(true)
  const tweetIds = ref<string[]>([])
  const accountsFilter = ref<'People' | 'Organizations' | 'People/Organizations'>('People/Organizations')
  const data = ref<Partial<StandaloneInsightsAnalyzer>>()
  const ageGroups = ref<StandAloneComputeAgeGroups['age_percentages']>()
  const ageGroupsConversationAudience = ref<StandAloneComputeAgeGroups['age_percentages']>()
  const dataConversation = ref<Partial<StandaloneXConversation>>()
  const dataConversationAudience = ref<Partial<StandaloneInsightsAnalyzer>>()
  const dataConversationAudienceError = ref<string>('')
  const analyzedKeywords = ref<string[][]>([])
  const excludeKeywords = ref<string[]>([])
  const conversationStart = ref<Date>(dayjs().subtract(30, 'days').toDate())
  const conversationEnd = ref<Date>(dayjs().subtract(5, 'minutes').toDate())
  const conversationOrderPosts = ref<StandaloneXConversationInput['order_posts']>('like_count')
  const country = ref<string[]>(['Global'])
  const countryTrend = ref<string>('United States')
  const extraKeywordType = ref<'and' | 'or' | 'exclude'>('and')
  const extraKeyword = ref('')
  const addingKeyword = ref(false)
  const selectedDownload = ref<string[]>([
    'profile',
    'posts',
    'audience_size',
    'posts',
    'similar_accounts',
    'top_accounts',
    'largest_followers',
    'follower_growth',
    'posts_analysis',
    'visual_highlights',
    'hashtags_used_by_handle',
    'keywords_used_by_handle',
    'keyword_combinations_used_by_handle',
    'sample_accounts',
    'countries',
    'languages',
    'genders',
    'age_groups',
    'seo',
    'cities',
    'interests_of_this_audience',
    'topics',
    'most_interacted_emojis',
    'most_interacted_hashtags',
    'most_interacted_keywords',
    'most_interacted_keyword_combinations',
    'hashtags_self_view',
    'audience_self_view',
    'audience_self_view_combinations',
    'job_titles',
    'subreddits',
    'conversation_volume',
    'sentiment_analysis',
    'conversation_posts',
    'share_of_voice',
  ])
  const downloadBackground = ref<'transparent' | 'black'>('black')
  const downloading = ref(false)
  const lastQuery = ref<string | string[] | Pick<StandaloneXConversationInput, 'keywords' | 'start' | 'end'>>('')
  const runningQuery = ref<string | string[] | Pick<StandaloneXConversationInput, 'keywords' | 'start' | 'end'>>()
  const queryType = ref<QueryType>('conversation')
  const submittedQueryType = ref<QueryType>(queryType.value)
  const friendlyQueryType = computed(() => {
    if (queryType.value === 'handle') {
      return 'Handle analysis'
    }

    if (queryType.value === 'keyword') {
      return 'Audience'
    }

    if (queryType.value === 'conversation') {
      return 'Conversation'
    }

    if (queryType.value === 'post') {
      return 'Posts'
    }

    throw new Error(`Unknown query type: ${queryType.value}`)
  })
  const placeholder = computed(() => {
    if (queryType.value === 'handle') {
      return '@socialdatabase'
    }

    if (queryType.value === 'conversation') {
      return '#football or super bowl or NFL...'
    }

    if (queryType.value === 'post') {
      return 'Post URLs, separated by commas...'
    }

    return 'Tennis fans...'
  })
  const range = ref<{ start: Dayjs; end: Dayjs }>()
  const shareOfConversation = ref<(StandAlonePostActivity & StandAloneShareOfVoice)[]>()
  const shareOfConversationAdd = ref('')
  const trendingTopics = ref<DeepThoughtXStatsTrend[]>([])
  const isOpenAi = ref(false)
  const aiReferences = ref<Record<string, any>[]>([])
  const aiReferencesLoading = ref(false)
  const showCategories = ref(false)
  const showTrendingTopics = ref(false)
  const analyzedQuery = ref<StandaloneExploreQueryAnalyzer | StandaloneExploreExtractDataTweetKeywords>()

  return {
    loading,
    loadingPosts,
    loadingSentiments,
    loadingConversation,
    loadingRelevantConfigs,
    loadingEnrichments,
    loadingPostMetadata,
    loadingSynonyms,
    loadingTrendingTopics,
    loadingShareOfVoice,
    takingTooLong,
    tookTooLong,
    tookTooLongAccounts,
    tookTooLongPosts,
    tookTooLongEnrichments,
    tookTooLongDemographics,
    tookTooLongAge,
    isOpenRequest,
    isOpenEmail,
    submittedHandle,
    handle,
    error,
    tweetIds,
    accountsFilter,
    data,
    ageGroups,
    synonyms,
    dataConversation,
    dataConversationAudience,
    dataConversationAudienceError,
    ageGroupsConversationAudience,
    excludeKeywords,
    analyzedKeywords,
    conversationStart,
    conversationEnd,
    conversationOrderPosts,
    country,
    countryTrend,
    extraKeywordType,
    extraKeyword,
    addingKeyword,
    selectedDownload,
    downloadBackground,
    downloading,
    lastQuery,
    runningQuery,
    friendlyQueryType,
    placeholder,
    submittedQueryType,
    queryType,
    range,
    shareOfVoiceWithKeywords,
    shareOfConversation,
    shareOfConversationAdd,
    trendingTopics,
    isOpenAi,
    aiReferences,
    aiReferencesLoading,
    showCategories,
    showTrendingTopics,
    similarHandles,
    analyzedQuery,
  }
})
